import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import { TaskTable } from "@/components/project/TaskTable";
import { TaskDialog } from "@/components/project";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useProjects } from "@/contexts/ProjectsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTaskDialog } from "@/contexts/TaskDialogContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];
type TaskRow = Database["public"]["Tables"]["tasks"]["Row"];
type TaskPartialUpdate = Partial<TaskRow> & { id: string };

export function Pending() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tasks, setTasks] = useState<TaskWithDetailsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithDetailsRow | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("all");
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>("all");
  const [projectMembers, setProjectMembers] = useState<
    Array<{
      user_id: string;
      username: string | null;
      full_name: string | null;
      avatar_url: string | null;
    }>
  >([]);
  const { projects, loading: projectsLoading } = useProjects();
  const { user, loading: authLoading } = useAuth();
  const { taskToOpen, clearTaskToOpen } = useTaskDialog();
  const hasFetchedRef = useRef(false);
  const lastProjectsRef = useRef<string>("");
  const previousProjectIdRef = useRef<string>("all");

  // Open task dialog when taskToOpen is set from context
  useEffect(() => {
    if (taskToOpen) {
      // Check if the task is in the current task list
      const taskExists = tasks.some((t) => t.id === taskToOpen.id);
      if (taskExists) {
        setEditingTask(taskToOpen);
        setTaskDialogOpen(true);
        clearTaskToOpen();
      } else {
        // Task not in current view, navigate to project
        if (taskToOpen.project_id) {
          navigate(`/project/${taskToOpen.project_id}`);
        }
        clearTaskToOpen();
      }
    }
  }, [taskToOpen, tasks, clearTaskToOpen, navigate]);

  const fetchTasks = useCallback(async () => {
    // Don't fetch if auth or projects are still loading
    if (authLoading || projectsLoading) return;

    setLoading(true);
    setError(null);

    try {
      // Get today's date in YYYY-MM-DD format (UTC)
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split("T")[0];

      // Use auth state from context
      if (!user) {
        setError("You must be logged in");
        setLoading(false);
        return;
      }

      const projectIds = projects?.map((p: { project_id: string }) => p.project_id) || [];

      if (projectIds.length === 0) {
        setTasks([]);
        setLoading(false);
        return;
      }

      // Get tasks that are:
      // 1. Not completed (status != 'done')
      // 2. Either have no due_date OR due_date has passed (due_date < today)
      // Use tasks_with_details view to get project and assigned user info in one query
      const { data, error: tasksError } = await supabase
        .from("tasks_with_details")
        .select("*")
        .in("project_id", projectIds)
        .neq("status", "done")
        .or(`due_date.is.null,due_date.lt.${todayStr}`)
        .order("due_date", { ascending: true, nullsFirst: true })
        .order("updated_at", { ascending: false });

      if (tasksError) {
        throw tasksError;
      }

      setTasks(data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load tasks";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [projects, user, authLoading, projectsLoading]);

  // Fetch tasks when auth and projects are ready
  useEffect(() => {
    // Skip if already loading or if contexts aren't ready
    if (authLoading || projectsLoading) return;

    // If no user, don't fetch
    if (!user) return;

    // Fetch when we have a user and projects are loaded
    // Track which projects we've fetched for to avoid unnecessary refetches
    if (user && projects) {
      const projectIds = projects
        .map((p: { project_id: string }) => p.project_id)
        .sort()
        .join(",");

      // Fetch if we haven't fetched yet, or if the projects have changed
      if (!hasFetchedRef.current || lastProjectsRef.current !== projectIds) {
        hasFetchedRef.current = true;
        lastProjectsRef.current = projectIds;
        fetchTasks();
      }
    }
  }, [fetchTasks, authLoading, projectsLoading, user, projects]);

  const handleMarkComplete = async (task: TaskWithDetailsRow) => {
    const newStatus = task.status === "done" ? "todo" : "done";
    const updatedTask = { ...task, status: newStatus, updated_at: new Date().toISOString() };

    // Optimistic update - remove from list if completed
    if (newStatus === "done") {
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } else {
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? (updatedTask as TaskWithDetailsRow) : t)),
      );
    }

    const { error } = await supabase
      .from("tasks")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", task.id!);

    if (error) {
      // Revert on error
      setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
      console.error("Error updating task:", error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm("Are you sure you want to delete this task?")) return;

    // Optimistic update
    const taskToDelete = tasks.find((t) => t.id === taskId);
    setTasks((prev) => prev.filter((t) => t.id !== taskId));

    const { error } = await supabase.from("tasks").delete().eq("id", taskId);

    if (error) {
      // Revert on error
      if (taskToDelete) {
        setTasks((prev) => [...prev, taskToDelete]);
      }
      console.error("Error deleting task:", error);
    }
  };

  const handleTaskCreated = (newTask: TaskWithDetailsRow | TaskPartialUpdate) => {
    // For create, we always receive full TaskWithDetailsRow from TaskDialog
    const taskWithDetails = newTask as TaskWithDetailsRow;

    // Check if task should be shown in Pending view
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split("T")[0];

    if (
      taskWithDetails.status !== "done" &&
      (!taskWithDetails.due_date || taskWithDetails.due_date < todayStr)
    ) {
      setTasks((prev) => {
        if (prev.find((t) => t.id === taskWithDetails.id)) return prev;
        return [...prev, taskWithDetails].sort((a, b) => {
          if (a.due_date && b.due_date) {
            return a.due_date.localeCompare(b.due_date);
          }
          if (!a.due_date && b.due_date) return 1;
          if (a.due_date && !b.due_date) return -1;
          return 0;
        });
      });
    }
  };

  const handleTaskUpdated = (updatedTask: TaskWithDetailsRow | TaskPartialUpdate) => {
    // If we received a partial update, merge only the updated fields
    if (!("project_name" in updatedTask) && updatedTask.id) {
      setTasks((prev) => {
        const existingTask = prev.find((t) => t.id === updatedTask.id);
        if (!existingTask) return prev;

        // Merge only the updated fields into existing task
        const mergedTask: TaskWithDetailsRow = {
          ...existingTask,
          ...updatedTask,
        };

        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split("T")[0];

        // If task should be shown in Pending view
        if (
          mergedTask.status !== "done" &&
          (!mergedTask.due_date || mergedTask.due_date < todayStr)
        ) {
          return prev.map((t) => (t.id === mergedTask.id ? mergedTask : t));
        } else {
          // Remove if it shouldn't be shown
          return prev.filter((t) => t.id !== mergedTask.id);
        }
      });
      return;
    }

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split("T")[0];

    // If task should be shown in Pending view (updatedTask is already TaskWithDetailsRow)
    const taskWithDetails = updatedTask as TaskWithDetailsRow;
    if (
      taskWithDetails.status !== "done" &&
      (!taskWithDetails.due_date || taskWithDetails.due_date < todayStr)
    ) {
      setTasks((prev) => prev.map((t) => (t.id === taskWithDetails.id ? taskWithDetails : t)));
    } else {
      // Remove if it shouldn't be shown
      setTasks((prev) => prev.filter((t) => t.id !== taskWithDetails.id));
    }
  };

  // Extract unique projects from tasks for filter options
  const uniqueProjects = Array.from(
    new Map(
      tasks
        .filter((t) => t.project_id && t.project_name)
        .map((t) => [t.project_id, { id: t.project_id, name: t.project_name }]),
    ).values(),
  );

  // Fetch project members when a project is selected
  useEffect(() => {
    const fetchMembers = async () => {
      if (selectedProjectId === "all") {
        setProjectMembers([]);
        // Only reset assignee filter when switching FROM a project TO "all projects"
        // Don't reset if we're already on "all projects" and user is just changing assignee
        // Also preserve "unassigned" option
        if (
          previousProjectIdRef.current !== "all" &&
          selectedAssigneeId !== "all" &&
          selectedAssigneeId !== "unassigned"
        ) {
          setSelectedAssigneeId("all");
        }
        previousProjectIdRef.current = "all";
        return;
      }

      try {
        const { data: membersData, error } = await supabase
          .from("project_members_with_profiles")
          .select("*")
          .eq("project_id", selectedProjectId)
          .order("role", { ascending: false })
          .order("added_at", { ascending: true });

        if (error) {
          console.error("Error fetching project members:", error);
          setProjectMembers([]);
        } else {
          setProjectMembers(
            (membersData || []).map((m) => ({
              user_id: m.user_id!,
              username: m.username,
              full_name: m.full_name,
              avatar_url: m.avatar_url,
            })),
          );
          // Reset assignee filter if current selection is not in the new project
          // But preserve "unassigned" option
          if (
            selectedAssigneeId !== "all" &&
            selectedAssigneeId !== "unassigned" &&
            !membersData?.some((m) => m.user_id === selectedAssigneeId)
          ) {
            setSelectedAssigneeId("all");
          }
        }
      } catch (err) {
        console.error("Error fetching project members:", err);
        setProjectMembers([]);
      }

      previousProjectIdRef.current = selectedProjectId;
    };

    fetchMembers();
  }, [selectedProjectId, selectedAssigneeId]);

  // Get assignee options - scoped to project members if a project is selected, otherwise all assignees from tasks
  const assigneeOptions =
    selectedProjectId !== "all" && projectMembers.length > 0
      ? projectMembers
          .map((m) => ({
            id: m.user_id,
            username: m.username || m.user_id || "",
            full_name: m.full_name,
          }))
          .filter((a) => a.id !== null)
      : Array.from(
          new Map(
            tasks
              .filter((t) => t.assigned_to)
              .map((t) => [
                t.assigned_to,
                {
                  id: t.assigned_to,
                  username: t.assigned_username || t.assigned_to || "",
                  full_name: t.assigned_full_name,
                },
              ]),
          ).values(),
        ).filter((a) => a.id !== null);

  // Apply filters
  const filteredTasks = tasks.filter((task) => {
    // Project filter
    if (selectedProjectId !== "all" && task.project_id !== selectedProjectId) {
      return false;
    }

    // Assignee filter
    if (selectedAssigneeId !== "all") {
      if (selectedAssigneeId === "unassigned") {
        // Show only tasks without an assignee
        if (task.assigned_to !== null) {
          return false;
        }
      } else {
        // Show only tasks assigned to the selected user
        if (task.assigned_to !== selectedAssigneeId) {
          return false;
        }
      }
    }

    return true;
  });

  if (loading && tasks.length === 0 && !hasFetchedRef.current) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">Pending</h1>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setEditingTask(null);
              setTaskDialogOpen(true);
            }}
            aria-label="Add task"
          >
            <Plus className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Add task</span>
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-muted-foreground">Loading tasks…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">Pending</h1>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setEditingTask(null);
              setTaskDialogOpen(true);
            }}
            aria-label="Add task"
          >
            <Plus className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Add task</span>
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-destructive">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <header className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0 ">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">Pending</h1>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setEditingTask(null);
              setTaskDialogOpen(true);
            }}
            aria-label="Add task"
          >
            <Plus className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Add task</span>
          </Button>
        </header>
        <main className={`flex-1 overflow-y-auto p-6 ${isMobile ? "pb-content-safe" : ""}`}>
          <div className="w-full space-y-4 min-w-0">
            <p className="text-sm text-muted-foreground">
              Tasks that are not completed and either have no due date or are overdue
            </p>

            {/* Control Bar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All projects</SelectItem>
                    {uniqueProjects.map((project) => (
                      <SelectItem key={project.id} value={project.id!}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedAssigneeId} onValueChange={setSelectedAssigneeId}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All assignees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All assignees</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {assigneeOptions
                      .filter(
                        (
                          assignee,
                        ): assignee is { id: string; username: string; full_name: string | null } =>
                          assignee.id !== null,
                      )
                      .map((assignee) => (
                        <SelectItem key={assignee.id} value={assignee.id}>
                          {assignee.full_name || assignee.username || "Unnamed"}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Task List */}
            <TaskTable
              tasks={filteredTasks}
              onEdit={(task) => {
                setEditingTask(task);
                setTaskDialogOpen(true);
              }}
              onMarkComplete={handleMarkComplete}
              onDelete={handleDeleteTask}
            />
          </div>
        </main>

        {/* Floating Action Button */}
        <Button
          onClick={() => {
            setEditingTask(null);
            setTaskDialogOpen(true);
          }}
          className={`fixed ${isMobile ? "bottom-safe-tab" : "bottom-8"} right-6 md:right-8 h-14 w-14 md:h-16 md:w-16 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-40`}
          size="icon"
          aria-label="Add new task"
        >
          <Plus className="h-6 w-6 md:h-7 md:w-7" />
        </Button>
      </div>

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          setTaskDialogOpen(open);
          if (!open) {
            setEditingTask(null);
          }
        }}
        onSuccess={(task) => {
          if (task) {
            if (editingTask) {
              handleTaskUpdated(task);
            } else {
              handleTaskCreated(task);
            }
          } else {
            fetchTasks();
          }
          setEditingTask(null);
        }}
        projectId={editingTask?.project_id ?? undefined}
        task={editingTask}
        mode={editingTask ? "edit" : "create"}
      />
    </>
  );
}